import { App, DirectiveBinding } from 'vue';
import { hasPermission, hasAnyPermission, hasAllPermissions } from '@/utils/permission';

/**
 * 权限指令 v-permission
 * 
 * 用法示例：
 * 1. 单个权限：<el-button v-permission="'system:user:add'">新增</el-button>
 * 2. 多个权限（满足任一）：<el-button v-permission="['system:user:add', 'system:user:edit']">操作</el-button>
 * 3. 使用修饰符：
 *    - v-permission.all="['add', 'edit']" - 需要所有权限
 *    - v-permission.any="['add', 'edit']" - 需要任一权限（默认）
 */
export function setupPermissionDirective(app: App) {
    app.directive('permission', {
        mounted(el: HTMLElement, binding: DirectiveBinding) {
            const { value, modifiers, arg } = binding;
            // 没有权限时移除元素的辅助函数
            const removeElement = () => {
                el.parentNode?.removeChild(el);
            };
            
            // 处理不同的使用方式
            if (!value) {
                return;
            }
            
            // 转换为数组处理
            const permissions = Array.isArray(value) ? value : [value];
            
            // 根据修饰符判断权限检查方式
            if (modifiers.all) {
                // 需要所有权限
                const hasAuth = hasAllPermissions(permissions);
                if (!hasAuth) {
                    removeElement();
                }
            } else if (modifiers.any || !arg) {
                // 需要任一权限（默认行为）
                const hasAuth = hasAnyPermission(permissions);
                if (!hasAuth) {
                    removeElement();
                }
            } else if (arg === 'single') {
                // 单个权限（明确指定）
                const hasAuth = hasPermission(permissions[0]);
                if (!hasAuth) {
                    removeElement();
                }
            } else {
                // 默认：任一权限
                const hasAuth = hasAnyPermission(permissions);
                if (!hasAuth) {
                    removeElement();
                }
            }
        }
    });
}

/**
 * Vue 插件形式导出
 */
export const PermissionDirective = {
    install(app: App) {
        setupPermissionDirective(app);
    }
};
